import { RoomGamePassPayment } from '@/components/room/room-game-pass-payment'
import { RoomChat } from '@/components/room/RoomChat'
import { useSingleFlight } from '@/lib/single-flight'
import { getStartedGameRoute } from '@/lib/started-game-route'
import { RoomNameEditor } from '@/components/room/RoomNameEditor'
import { RoomSetup } from '@/components/room/RoomSetup'
import { RoomPaymentLabel } from '@/components/room/room-payment-label'
import { InlineRegistration } from '@/components/inline-registration'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { buildInviteUrl } from '@/lib/arena'
import { useAuth } from '@/lib/auth'
import { useAccountTimeZone } from '@/hooks/use-account-time-zone'
import { useI18n } from '@/lib/i18n'
import { shouldConfirmStart } from '@/lib/room-exposure'
import { returnTargetFrom, signInUrlReturningTo, verifyEmailUrlReturningTo } from '@/lib/return-target'
import { useTRPC } from '@/lib/trpc'
import { stripLanguagePrefix } from '@/lib/url-language-utils'
import { cn } from '@/lib/utils'
import { TRPCClientError } from '@trpc/client'
import { isRegisteredAccount } from '@repo/sdk'

import {
  roomRefetchIntervalMs,
  roomStreamDeliversUpdates,
  useRoomStreamEligibility,
} from '@/lib/room-stream'
import { useSubscription } from '@trpc/tanstack-react-query'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

export function RoomPage() {
  const trpc = useTRPC()
  const { t, language } = useI18n()
  const { actor, authResolved } = useAuth()
  const accountCalendar = useAccountTimeZone()
  const initializeAccountTimeZone = accountCalendar.initialize
  const navigate = useNavigate()
  const { code = '' } = useParams<{ code: string }>()

  const toastError = useCallback(
    (error: unknown) => {
      if (!(error instanceof TRPCClientError)) {
        toast.error(t('game.error.start_game_failed'))
        return
      }
      const raw = error.message || 'start_game_failed'
      if (raw === 'game_pass_unavailable' || raw === 'game_pass_ineligible') {
        toast.error(t(`gamePasses.errors.${raw}`))
        return
      }
      const translated = t(`game.error.${raw}`)
      toast.error(translated !== `game.error.${raw}` ? translated : t('game.error.start_game_failed'))
    },
    [t],
  )

  const eligibleForStream = useRoomStreamEligibility(actor, authResolved)
  const [streamLive, setStreamLive] = useState(false)
  const streaming = eligibleForStream && streamLive

  const statusQuery = useQuery({
    ...trpc.roomRouter.status.queryOptions({ roomId: code }),
    enabled: !!code && !!actor,
    refetchInterval: (q) => {
      const status = q?.state?.data?.room?.status
      return roomRefetchIntervalMs({
        streaming,
        terminal:
          status === 'playing'
          || status === 'cancelled'
          || status === 'completed',
      })
    },
    refetchIntervalInBackground: true,
  })

  const [roomChatRevision, setRoomChatRevision] = useState(0)

  const isRoomMember = Boolean(
    actor && statusQuery.data?.members?.some(member => member.userId === actor.id),
  )

  const roomSubscription = useSubscription(
    trpc.roomRouter.onRoomChanged.subscriptionOptions(
      { roomId: statusQuery.data?.room?.id ?? '' },
      {
        enabled: eligibleForStream && isRoomMember && !!statusQuery.data?.room?.id,
        onData: (frame) => {
          if (frame.type === 'room.changed' || frame.type === 'stream.resync')
            void statusQuery.refetch()
          if (frame.type === 'room.chat.changed' || frame.type === 'stream.resync')
            setRoomChatRevision(n => n + 1)
        },
        onError: () => {},
        onConnectionStateChange: (state) => {
          if (state.state === 'pending') {
            void statusQuery.refetch()
            setRoomChatRevision(n => n + 1)
          }
        },
      },
    ),
  )

  useEffect(() => {
    setStreamLive(
      roomStreamDeliversUpdates({
        eligible: eligibleForStream,
        status: roomSubscription.status,
      }),
    )
  }, [eligibleForStream, roomSubscription.status])

  const joinedRoomIdRef = useRef<string | null>(null)
  const joinRetryTimerRef = useRef<number | null>(null)
  useEffect(() => () => {
    if (joinRetryTimerRef.current !== null)
      window.clearTimeout(joinRetryTimerRef.current)
  }, [])
  const joinMutation = useMutation(trpc.roomRouter.join.mutationOptions())
  const { mutate: joinRoom } = joinMutation
  useEffect(() => {
    const room = statusQuery.data?.room
    if (
      !isRegisteredAccount(actor)
      || !room
      || !accountCalendar.ready
      || room.status !== 'lobby'
      || isRoomMember
      || (!room.freeGame && room.paymentMode === 'split')
      || joinedRoomIdRef.current === room.id
    ) {
      return
    }
    const ownerUserId = statusQuery.data?.room?.ownerUserId
    if (!ownerUserId)
      return
    if (!actor || ownerUserId === actor.id)
      return
    if (!actor.gameIdentityVerified) {
      if (!isRoomMember)
        navigate(verifyEmailUrlReturningTo(returnTargetFrom(window.location)), { replace: true })
      return
    }
    joinedRoomIdRef.current = room.id
    joinRoom(
      { roomId: room.id },
      {
        onError: (error) => {
          const isAlreadyMember
            = error instanceof TRPCClientError
              && (error.data?.code === 'CONFLICT' || error.message === 'already_member')
          if (isAlreadyMember)
            return
          toastError(error)
          if (joinRetryTimerRef.current !== null)
            window.clearTimeout(joinRetryTimerRef.current)
          joinRetryTimerRef.current = window.setTimeout(() => {
            if (joinedRoomIdRef.current === room.id)
              joinedRoomIdRef.current = null
            joinRetryTimerRef.current = null
          }, 5000)
        },
      },
    )
  }, [
    accountCalendar.ready,
    actor,
    isRoomMember,
    joinRoom,
    navigate,
    toastError,
    statusQuery.data?.room,
    statusQuery.dataUpdatedAt,
  ])

  const [startError, setStartError] = useState('')
  const [paymentPending, setPaymentPending] = useState(false)
  const [showExposureDialog, setShowExposureDialog] = useState(false)
  const startMutation = useMutation(trpc.roomRouter.start.mutationOptions())
  const cancelMutation = useMutation(trpc.roomRouter.cancel.mutationOptions())
  const leaveMutation = useMutation(trpc.roomRouter.leave.mutationOptions())

  const room = statusQuery.data?.room
  const members = statusQuery.data?.members ?? []
  const seatPreview = statusQuery.data?.seatPreview
  const canonicalCode = statusQuery.data?.roomCode ?? code
  const aiCount = seatPreview ? Math.max(0, seatPreview.total - seatPreview.humanCount) : 0
  const isHost = !!room && !!actor && room.ownerUserId === actor.id

  const [wasSeatedHere, setWasSeatedHere] = useState(false)
  const seatedInThisRoom
    = !!room && !!actor && members.some(member => member.userId === actor.id)
  useEffect(() => {
    if (seatedInThisRoom)
      setWasSeatedHere(true)
  }, [seatedInThisRoom])

  const removeMemberMutation = useMutation({
    ...trpc.roomRouter.removeMember.mutationOptions(),
    onError: toastError,
  })

  const wasRemoved
    = !!room
      && room.status === 'lobby'
      && !!actor
      && !isHost
      && wasSeatedHere
      && !seatedInThisRoom
  const hostName = (members.find(m => m.userId === room?.ownerUserId)?.name ?? '').trim()

  const navigatedRef = useRef(false)
  useEffect(() => {
    if ((room?.status === 'playing' || room?.status === 'completed') && room.gameId && !navigatedRef.current
      && !stripLanguagePrefix(window.location.pathname).startsWith('/play')) {
      navigatedRef.current = true
      navigate(getStartedGameRoute(room.gameId, room.gameSetting ?? ''))
    }
  }, [navigate, room?.gameId, room?.gameSetting, room?.status])

  const startLatch = useSingleFlight()

  const handleStart = useCallback(async () => {
    if (!room || paymentPending)
      return
    if (actor && !actor.isAnonymous && !actor.gameIdentityVerified) {
      navigate(verifyEmailUrlReturningTo(returnTargetFrom(window.location)))
      return
    }
    if (!startLatch.tryAcquire())
      return
    setStartError('')
    try {
      await initializeAccountTimeZone()
      await startMutation.mutateAsync({ roomId: room.id })
    }
    catch (error) {
      if (error instanceof TRPCClientError)
        toastError(error)
      const key = error instanceof TRPCClientError ? error.message : ''
      const passError = key === 'game_pass_unavailable' || key === 'game_pass_ineligible'
      const translated = key ? t(passError ? `gamePasses.errors.${key}` : `game.error.${key}`) : ''
      setStartError(translated && translated !== `game.error.${key}`
        ? translated
        : t('game.room.startFailed'))
    }
    finally {
      startLatch.release()
    }
  }, [
    initializeAccountTimeZone,
    actor,
    navigate,
    paymentPending,
    room,
    startLatch,
    startMutation,
    t,
    toastError,
  ])

  const requestStart = useCallback(() => {
    if (actor && !actor.isAnonymous && !actor.gameIdentityVerified) {
      navigate(verifyEmailUrlReturningTo(returnTargetFrom(window.location)))
      return
    }
    if (shouldConfirmStart(statusQuery.data?.hostExposure)) {
      setShowExposureDialog(true)
      return
    }
    handleStart()
  }, [actor, handleStart, navigate, statusQuery.data?.hostExposure])

  const confirmStart = useCallback(() => {
    setShowExposureDialog(false)
    handleStart()
  }, [handleStart])

  const handleLeave = useCallback(() => {
    if (!room)
      return
    leaveMutation.mutate(
      { roomId: room.id },
      {
        onError: toastError,
        onSuccess: () => {
          navigate('/?arena')
        },
      },
    )
  }, [leaveMutation, navigate, room, toastError])

  const handleCancel = useCallback(() => {
    if (!room)
      return
    cancelMutation.mutate(
      { roomId: room.id },
      { onError: toastError },
    )
  }, [cancelMutation, room, toastError])

  const previewQuery = useQuery({
    ...trpc.roomRouter.publicRoomPreview.queryOptions({ roomCode: code }),
    enabled: authResolved && !actor && !!code,
    retry: false,
    refetchOnWindowFocus: false,
  })

  const handleCopyLink = useCallback(async () => {
    const link = buildInviteUrl(canonicalCode)
    try {
      await navigator.clipboard.writeText(link)
      toast.success(t('game.room.linkCopied'))
    }
    catch {}
  }, [canonicalCode, t])

  if (!authResolved) {
    return (
      <div className="flex min-h-screen w-full items-center justify-center px-4 pt-20">
        <Card className="w-full max-w-md">
          <CardContent className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            {t('game.room.loading')}
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!actor) {
    const preview = previewQuery.data
    return (
      <div className="flex min-h-screen w-full items-center justify-center px-4 pt-20">
        <Card className="w-full max-w-md" data-testid="room-signed-out">
          <CardHeader>
            <CardTitle>{t('game.room.roomTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {preview && (
              <div className="space-y-1 text-sm text-muted-foreground">
                <p data-testid="room-preview-seats">
                  {preview.humanCount}
                  {preview.capacity === null ? '' : ` / ${preview.capacity}`}
                </p>
                <RoomPaymentLabel
                  freeGame={preview.freeGame}
                  paymentMode={preview.paymentMode}
                />
              </div>
            )}
            <p className="text-sm text-muted-foreground">
              {t('game.error.registration_required')}
            </p>
            {
            }
            <InlineRegistration
              returnTarget={returnTargetFrom(window.location)}
              onRegistered={async () => {
                await statusQuery.refetch()
              }}
            />
            <Button
              variant="ghost"
              className="w-full"
              data-testid="room-sign-in"
              onClick={() =>
                navigate(signInUrlReturningTo(returnTargetFrom(window.location)))}
            >
              {t('nav.signIn')}
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  const statusError = statusQuery.error
  if (
    statusError instanceof TRPCClientError
    && (statusError.data?.code === 'NOT_FOUND' || statusError.message === 'room_not_found')
  ) {
    return (
      <div className="flex min-h-screen w-full items-center justify-center px-4 pt-20">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle data-testid="room-not-found">{t('game.room.notFound')}</CardTitle>
          </CardHeader>
        </Card>
      </div>
    )
  }

  const chatVisible = Boolean(statusQuery.data?.room?.id)

  return (
    <div className="flex min-h-screen w-full items-start justify-center px-4 pb-72 pt-20 sm:pb-40">
      {
      }
      <div className={cn(
        'grid w-full gap-4',
        chatVisible ? 'max-w-4xl lg:grid-cols-[minmax(0,28rem)_minmax(0,1fr)]' : 'max-w-md',
      )}
      >
        <Card className="w-full">
          <CardHeader>
            <CardTitle>{t('game.room.roomTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-6">
            {(wasRemoved || room?.status === 'cancelled' || room?.status === 'completed')
              ? (
                  <p
                    data-testid={
                      wasRemoved
                        ? 'room-removed'
                        : room?.status === 'cancelled'
                          ? 'room-cancelled'
                          : 'room-completed'
                    }
                    className="text-sm text-muted-foreground"
                  >
                    {wasRemoved
                      ? t('game.room.removed')
                      : room?.status === 'cancelled'
                        ? t('game.room.cancelled')
                        : t('play.gameCompleted')}
                  </p>
                )
              : (
                  <>
                    {
                    }
                    <RoomSetup
                      gameSetting={room?.gameSetting}
                      seatCount={seatPreview?.total}
                    />

                    <RoomNameEditor
                      roomId={room?.id ?? null}
                      name={room?.name ?? null}
                      canEdit={isHost && room?.status === 'lobby'}
                      onSaved={() => { void statusQuery.refetch() }}
                    />

                    <RoomPaymentLabel
                      freeGame={room?.freeGame}
                      paymentMode={room?.paymentMode}
                    />

                    {room?.status === 'lobby' && statusQuery.data?.payment?.canChoose
                      && (statusQuery.data.payment.source === 'credits' || statusQuery.data.payment.source === 'game-pass') && (
                      <RoomGamePassPayment
                        key={`${room.id}:${actor.id}`}
                        roomId={room.id}
                        policy={room.paymentMode === 'host' ? 'host' : 'split'}
                        payment={{ source: statusQuery.data.payment.source, isMember: statusQuery.data.payment.isMember }}
                        onSaved={async () => { setStartError(''); await statusQuery.refetch() }}
                        onPendingChange={setPaymentPending}
                      />
                    )}
                    {room?.status === 'lobby' && statusQuery.data?.payment?.source === 'sponsored' && (
                      <p data-testid="game-pass-room-sponsored" dir={language === 'ar' ? 'rtl' : 'ltr'} className="text-sm text-muted-foreground">{t('gamePasses.room.sponsored')}</p>
                    )}

                    { }
                    <div className="flex flex-col gap-2">
                      <span className="text-sm font-medium">{t('game.room.inviteLink')}</span>
                      <div className="flex gap-2">
                        <Input
                          readOnly
                          value={buildInviteUrl(canonicalCode)}
                          data-testid="invite-link"
                          onFocus={e => e.currentTarget.select()}
                        />
                        <Button
                          type="button"
                          variant="default"
                          onClick={handleCopyLink}
                          data-testid="copy-link-button"
                        >
                          {t('game.room.copyLink')}
                        </Button>
                      </div>
                    </div>

                    { }
                    <div className="flex flex-col gap-2">
                      <span className="text-sm font-medium">{t('game.room.members')}</span>
                      {seatPreview && (
                        <span className="block text-sm text-muted-foreground" data-testid="seats-preview">
                          {t('game.room.seatsAiFill', {
                            humans: seatPreview.humanCount,
                            total: seatPreview.total,
                          })}
                        </span>
                      )}
                      <ul className="flex max-h-[28rem] flex-col gap-1 overflow-y-auto" data-testid="member-list">
                        {members.map((member, index) => {
                          const isCurrentUser = member.userId === actor.id
                          const displayName = member.name || (isCurrentUser ? t('game.room.you') : t('game.room.playerN', { n: index + 1 }))
                          return (
                            <li
                              key={member.id}
                              data-testid="member-row"
                              aria-current={isCurrentUser ? 'true' : undefined}
                              className={cn(
                                'rounded-md px-3 py-2 text-sm',
                                isCurrentUser
                                  ? 'border border-primary/50 bg-primary/10 font-medium text-primary shadow-sm'
                                  : 'bg-muted/50',
                              )}
                            >
                              <div className="flex min-w-0 items-center justify-between gap-2">
                                <span className="truncate">{displayName}</span>
                                {isCurrentUser && member.name && (
                                  <Badge data-testid="member-you-badge" variant="default" className="shrink-0">
                                    {t('game.room.you')}
                                  </Badge>
                                )}
                                {
                                }
                                {isHost && !isCurrentUser && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    data-testid="remove-member-button"
                                    data-member-id={member.userId}
                                    disabled={removeMemberMutation.isPending}
                                    className="h-7 shrink-0 px-2 text-xs"
                                    onClick={() =>
                                      removeMemberMutation.mutate({
                                        roomId: room.id,
                                        userId: member.userId,
                                      })}
                                  >
                                    {t('game.room.removeMember')}
                                  </Button>
                                )}
                              </div>
                            </li>
                          )
                        })}
                        {
                        }
                        {seatPreview && Array.from({ length: aiCount }).map((_, i) => (
                          <li
                            key={`ai-${i}`}
                            data-testid="seat-placeholder"
                            className="rounded-md border border-dashed border-muted-foreground/40 px-3 py-2 text-sm text-muted-foreground"
                          >
                            {t('game.room.aiSeat')}
                          </li>
                        ))}
                      </ul>
                    </div>

                    { }
                    {isHost
                      ? (
                          <div className="flex flex-col gap-2">
                            <div className="flex gap-2">
                              <Button
                                type="button"
                                className="flex-1"
                                onClick={requestStart}
                                disabled={startMutation.isPending || paymentPending}
                                data-testid="start-room-button"
                              >
                                {startMutation.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
                                {t('game.room.start')}
                              </Button>
                              <AlertDialog
                                open={showExposureDialog}
                                onOpenChange={setShowExposureDialog}
                              >
                                <AlertDialogContent data-testid="paid-exposure-dialog">
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>
                                      {t(room?.paymentMode === 'host'
                                        ? 'game.room.hostPaidExposureWarningTitle'
                                        : 'game.room.paidExposureWarningTitle')}
                                    </AlertDialogTitle>
                                    <AlertDialogDescription>
                                      {t(room?.paymentMode === 'host'
                                        ? 'game.room.hostPaidExposureWarning'
                                        : 'game.room.paidExposureWarning', {
                                        humans: String(
                                          statusQuery.data?.hostExposure?.humanCount ?? 0,
                                        ),
                                      })}
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel data-testid="paid-exposure-cancel">
                                      {t('game.room.paidExposureCancel')}
                                    </AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={confirmStart}
                                      data-testid="paid-exposure-confirm"
                                    >
                                      {t('game.room.paidExposureConfirm')}
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                              {
                              }
                              <Button
                                type="button"
                                variant="outline"
                                onClick={handleCancel}
                                disabled={cancelMutation.isPending}
                                data-testid="cancel-room-button"
                              >
                                {cancelMutation.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
                                {t('game.room.cancel')}
                              </Button>
                            </div>
                            {startError && (
                              <p
                                role="alert"
                                data-testid="start-error"
                                className="text-sm text-red-600 dark:text-red-400"
                              >
                                {startError}
                              </p>
                            )}
                          </div>
                        )
                      : (
                          <div className="flex flex-col gap-2">
                            <p data-testid="waiting-for-host" className="text-sm text-muted-foreground">
                              {hostName
                                ? t('game.room.waitingForHostNamed', { host: hostName })
                                : t('game.room.waitingForHost')}
                            </p>
                            {
                            }
                            {seatedInThisRoom && room?.status === 'lobby' && (
                              <Button
                                type="button"
                                variant="outline"
                                onClick={handleLeave}
                                disabled={leaveMutation.isPending}
                                data-testid="leave-room-button"
                              >
                                {leaveMutation.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
                                {t('game.room.leave')}
                              </Button>
                            )}
                          </div>
                        )}
                  </>
                )}
          </CardContent>
        </Card>

        {chatVisible && statusQuery.data?.room?.id && (
          <RoomChat
            roomId={statusQuery.data.room.id}
            isOpen={statusQuery.data.room.status === 'lobby'}
            refreshKey={roomChatRevision}
            streaming={streaming}
          />
        )}
      </div>
    </div>
  )
}
